// Frontend interface for metric definitions
// SINGLE SOURCE OF TRUTH: Database table `metric_definitions`
// This file provides runtime access to metric metadata from the database

import { supabase } from "@/integrations/supabase/client";

export type MetricType = 'numeric' | 'boolean';
export type MetricCategory = 'nutrition' | 'activity_sleep' | 'results' | 'healthkit' | 'other';

export interface MetricDefinition {
  key: string;
  name: string;
  label: string;
  emoji: string;
  unit: string;
  type: MetricType;
  category: MetricCategory;
  description?: string;
  goalKey?: string;
  dbField?: string;
  readOnly?: boolean;
  isHealthKit?: boolean;
  preventQuickEntryDelete?: boolean;
  defaultGoal?: number;
  isInverse?: boolean;
  isRelative?: boolean; // When true, uses min/max coloring instead of goal-based
}

// Database table row structure
export interface MetricDefinitionRow {
  metric_key: string;
  label: string;
  emoji: string;
  unit: string;
  metric_type: 'numeric' | 'boolean';
  category: MetricCategory;
  default_goal: number | null;
  is_healthkit: boolean;
  goal_key: string | null;
  has_goal: boolean;
  is_inverse: boolean;
  is_relative: boolean;
}

// Cached metric definitions (populated on first fetch)
let cachedMetrics: Record<string, MetricDefinition> | null = null;
let cachePromise: Promise<Record<string, MetricDefinition>> | null = null;

/**
 * Fetch all metric definitions from database (SINGLE SOURCE OF TRUTH)
 * Results are cached for performance
 */
export async function fetchMetricDefinitions(): Promise<Record<string, MetricDefinition>> {
  // Return cached if available
  if (cachedMetrics) return cachedMetrics;
  
  // Return in-flight promise if already fetching
  if (cachePromise) return cachePromise;
  
  // Fetch from database using RPC function (workaround for PostgREST table exposure issue)
  cachePromise = (async () => {
    try {
      const { data, error } = await supabase.rpc('get_metric_definitions');
      
      if (error) {
        console.error('[MetricDefinitions] RPC error:', error);
        // Clear cache promise on error so it can be retried
        cachePromise = null;
        // Throw error so React Query can retry
        throw new Error(`Failed to fetch metric definitions: ${error.message}`);
      }
      
      if (!data || data.length === 0) {
        console.error('[MetricDefinitions] No metric definitions found in database - this is a critical error!');
        cachePromise = null;
        // Throw error so React Query can retry
        throw new Error('No metric definitions found in database');
      }
      
      console.log(`[MetricDefinitions] Successfully fetched ${data.length} metric definitions`);
      
      // Transform database rows to MetricDefinition format
      const metrics: Record<string, MetricDefinition> = {};
      for (const row of (data || [])) {
        // Validate required fields
        if (!row.label || row.label.trim() === '') {
          console.error(`[MetricDefinitions] CRITICAL: Missing label for metric ${row.metric_key}. Database is corrupt!`);
          throw new Error(`Missing label for metric ${row.metric_key}. Run RECREATE-METRIC-DEFINITIONS.sql`);
        }
        
        if (!row.emoji || row.emoji.trim() === '') {
          console.error(`[MetricDefinitions] CRITICAL: Missing emoji for metric ${row.metric_key}. Database is corrupt!`);
          throw new Error(`Missing emoji for metric ${row.metric_key}. Run RECREATE-METRIC-DEFINITIONS.sql`);
        }
        
        // Validate goal_key for metrics that should have goals
        if (row.has_goal && row.metric_type === 'numeric' && (!row.goal_key || row.goal_key.trim() === '')) {
          console.error(`[MetricDefinitions] CRITICAL: Missing goal_key for metric ${row.metric_key}. Database is corrupt!`);
          throw new Error(`Missing goal_key for metric ${row.metric_key}. Run RECREATE-METRIC-DEFINITIONS.sql`);
        }
        
        metrics[row.metric_key] = {
          key: row.metric_key,
          name: row.label,
          label: row.label,
          emoji: row.emoji,
          unit: row.unit,
          type: row.metric_type,
          category: row.category,
          goalKey: row.goal_key || undefined,
          dbField: row.metric_key,
          isHealthKit: row.is_healthkit,
          defaultGoal: row.default_goal || undefined,
          isInverse: row.is_inverse || false,
        };
      }
      
      cachedMetrics = metrics;
      cachePromise = null;
      return metrics;
    } catch (err) {
      console.error('[MetricDefinitions] Exception fetching metric definitions:', err);
      // Clear cache promise on exception so it can be retried
      cachePromise = null;
      cachedMetrics = null;
      // Re-throw so React Query can retry
      throw err;
    }
  })();
  
  return cachePromise;
}

/**
 * Clear the cache (use when metric definitions are updated)
 */
export function clearMetricDefinitionsCache() {
  cachedMetrics = null;
  cachePromise = null;
}

// ========================================
// ASYNC HELPER FUNCTIONS (Database-driven)
// ========================================

/**
 * Get a single metric definition by key
 */
export async function getMetric(key: string): Promise<MetricDefinition | undefined> {
  const metrics = await fetchMetricDefinitions();
  return metrics[key];
}

/**
 * Get all metric definitions as an array
 */
export async function getAllMetrics(): Promise<MetricDefinition[]> {
  const metrics = await fetchMetricDefinitions();
  return Object.values(metrics);
}

/**
 * Get metrics filtered by category (async version that fetches from database)
 */
export async function getMetricsByCategory(category: MetricCategory): Promise<MetricDefinition[]> {
  const all = await getAllMetrics();
  return all.filter(m => m.category === category);
}

/**
 * Get metrics filtered by type
 */
export async function getMetricsByType(type: MetricType): Promise<MetricDefinition[]> {
  const all = await getAllMetrics();
  return all.filter(m => m.type === type);
}

/**
 * Get only HealthKit metrics
 */
export async function getHealthKitMetrics(): Promise<MetricDefinition[]> {
  const all = await getAllMetrics();
  return all.filter(m => m.isHealthKit === true);
}

/**
 * Get only numeric metrics with goals
 */
export async function getNumericMetricsWithGoals(): Promise<MetricDefinition[]> {
  const all = await getAllMetrics();
  return all.filter(m => m.type === 'numeric' && m.goalKey);
}

/**
 * Check if a key is a built-in metric
 */
export async function isBuiltInMetric(key: string): Promise<boolean> {
  const metrics = await fetchMetricDefinitions();
  return key in metrics;
}

/**
 * Get default goals for all metrics (keyed by goalKey like 'calories_goal')
 */
export async function getDefaultGoals(): Promise<Record<string, number>> {
  const all = await getAllMetrics();
  const defaults: Record<string, number> = {};
  for (const metric of all) {
    if (metric.goalKey && metric.defaultGoal !== undefined) {
      defaults[metric.goalKey] = metric.defaultGoal;
    }
  }
  return defaults;
}
